import { useCallback, useState } from 'react'
import CommunicationPreferences from '../../containers/account/modals/communication-preferences'
import UpdatePassword from '../../containers/account/modals/update-password'
import UpdateDetails from '../../containers/account/modals/update-details'
import UpdateDealershipDetails from '../../containers/account/modals/update-dealership-details'
import FinancialPreferences from '../../containers/account/modals/financial-preferences'
import Router from 'next/router'
import AccountAvatar from '../../components/account-avatar'
import ShieldMark from '../../components/svg/shieldmark'
import Checkmark from '../../components/svg/checkmark'
import { useAuth } from '../../contexts/auth'
import { Button } from '../../components/button'
import { Mobile } from '../../components/media'
import { ROUTES } from '../../constants'
import { getYear, parseISO } from '../../utils/date-service'
import Credit from '../../components/svg/credit'
import { checkPreQualifyPermissions } from '../../utils/permissions'

export default function UserCard({
  onBack,
  CTAButtonText,
  onCTAButtonClick,
  handleAccountDetailsChange,
  handleDealershipDetailsChange,
}) {
  const [{ userDetails, preQualifyDetails }] = useAuth()

  const [showCommunicationPreferencesModal, setShowCommunicationPreferencesModal] = useState(false)
  const [showUpdateDetailsModal, setShowUpdateDetailsModal] = useState(false)
  const [showUpdateDealershipDetailsModal, setShowUpdateDealershipDetailsModal] = useState(false)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [showUpdateFianancialPreferencesModal, setShowUpdateFianancialPreferencesModal] = useState(
    false,
  )

  const isPreQualified = preQualifyDetails !== null
  const joinedInYear = getYear(parseISO(userDetails.joinDate, { withTime: true }))

  const handleGetVerifiedClick = useCallback(() => {
    Router.push(ROUTES.GetVerified)
  }, [])

  const handlePasswordChangeModalOpen = () => setShowChangePasswordModal(true)
  const handlePasswordChangeModalClose = useCallback(() => setShowChangePasswordModal(false), [])
  const handleUpdateDetailsModalOpen = () => setShowUpdateDetailsModal(true)
  const handleUpdateDetailsModalClose = useCallback(() => setShowUpdateDetailsModal(false), [])
  const handleUpdateDealershipDetailsModalOpen = () => setShowUpdateDealershipDetailsModal(true)
  const handleUpdateDealershipDetailsModalClose = useCallback(
    () => setShowUpdateDealershipDetailsModal(false),
    [],
  )

  const handleCommunicationPreferencesModalOpen = () => setShowCommunicationPreferencesModal(true)
  const handleCommunicationPreferencesModalClose = useCallback(
    () => setShowCommunicationPreferencesModal(false),
    [],
  )
  const handleUpdateFinancialPreferencesModalOpen = () =>
    setShowUpdateFianancialPreferencesModal(true)
  const handleUpdateFinancialPreferencesModalClose = useCallback(
    () => setShowUpdateFianancialPreferencesModal(false),
    [],
  )

  const handleGetPreQualifiedClick = useCallback(() => {
    Router.push(ROUTES.PreQualify)
  }, [])

  return (
    <div className='userCard'>
      <div className='wrapper'>
        <Mobile>
          <button onClick={onBack} className='flatBtn backToAccount'>
            &#60; Back to Account
          </button>
        </Mobile>

        <AccountAvatar />

        <div className='name'>{userDetails.firstName}</div>
        <div className='joinDate'>Joined in {joinedInYear}</div>

        <div className='status-wrapper'>
          <div className='rowItem'>
            <ShieldMark
              width='20'
              height='20'
              fill={userDetails.verified ? 'var(--success-color)' : 'var(--secondary-text-color)'}
            />
            {userDetails.verified ? (
              <span>Verified</span>
            ) : (
              <>
                <span>Needs Verification</span>
                <Button className='rowItemBtn' size='sm' onClick={handleGetVerifiedClick}>
                  Get Verified
                </Button>
              </>
            )}
          </div>
        </div>

        {checkPreQualifyPermissions(userDetails) && (
          <div className='status-wrapper'>
            <div className='rowItem'>
              <Credit
                width='20'
                height='20'
                fill={isPreQualified ? 'var(--success-color)' : 'var(--secondary-text-color)'}
              />
              <span style={{ marginRight: 3 }}>
                {isPreQualified ? 'Pre-Qualified' : 'Needs Pre-Qualification'}
              </span>

              <Button
                disabled={!userDetails.verified}
                className='rowItemBtn'
                size='sm'
                onClick={handleGetPreQualifiedClick}
              >
                {isPreQualified ? 'View' : 'Get Started'}
              </Button>
            </div>
          </div>
        )}

        <div className='confirmedRequisites'>
          <p className='requisitesTitle'>You Confirmed</p>
          <div className='email rowItem'>
            <Checkmark
              width='20'
              height='20'
              fill={
                userDetails.emailConfirmed ? 'var(--success-color)' : 'var(--secondary-text-color)'
              }
            />
            <span>Email Address</span>
          </div>
        </div>
        <div className='settings'>
          <button className='flatBtn' onClick={handleCommunicationPreferencesModalOpen}>
            Communication Preferences
          </button>
          <button className='flatBtn' onClick={handleUpdateDetailsModalOpen}>
            Account Information
          </button>
          {userDetails.isDealer && (
            <button className='flatBtn' onClick={handleUpdateDealershipDetailsModalOpen}>
              Dealership Information
            </button>
          )}
          <button className='flatBtn' onClick={handlePasswordChangeModalOpen}>
            Change Password
          </button>
          <button className='flatBtn' onClick={handleUpdateFinancialPreferencesModalOpen}>
            Change Financial Preferences
          </button>
        </div>
        <div className='buttonsContainer flex alignCenter justifyCenter'>
          <Button onClick={onCTAButtonClick}>{CTAButtonText}</Button>
        </div>

        {showChangePasswordModal && <UpdatePassword onClose={handlePasswordChangeModalClose} />}
        {showUpdateDetailsModal && (
          <UpdateDetails
            onClose={handleUpdateDetailsModalClose}
            onDetailsChange={handleAccountDetailsChange}
          />
        )}
        {showUpdateDealershipDetailsModal && (
          <UpdateDealershipDetails
            onClose={handleUpdateDealershipDetailsModalClose}
            onDetailsChange={handleDealershipDetailsChange}
          />
        )}
        {showCommunicationPreferencesModal && (
          <CommunicationPreferences onClose={handleCommunicationPreferencesModalClose} />
        )}
        {showUpdateFianancialPreferencesModal && (
          <FinancialPreferences onClose={handleUpdateFinancialPreferencesModalClose} />
        )}
      </div>

      <style jsx>{`
        .userCard {
          position: relative;
          max-width: 42rem;
          min-width: 42rem;
          padding: 0 0 2.1rem;
          font-weight: 700;

          .wrapper {
            width: 100%;
            padding: 2.3rem 2rem 5.1rem;
            border-radius: 1rem;
            background: var(--secondary-bg-color);
            box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.16);
          }
        }

        .name {
          margin: 1rem 0 0;
          text-align: center;
          font-size: 4.4rem;
        }

        .joinDate {
          margin: 0 0 3.3rem;
          color: var(--secondary-text-color);
          text-align: center;
          font-size: 1.6rem;
        }

        .status-wrapper {
          margin: 0 0 2.2rem;
        }

        .confirmedRequisites {
          padding: 2.2rem 0 0;
          border-top: 0.1rem solid var(--separator-line-color);

          .requisitesTitle {
            margin: 0 0 1.8rem;
            color: var(--secondary-text-color);
            font-size: 1.6rem;
          }
        }

        .rowItem {
          display: flex;
          align-items: center;

          :global(svg) {
            margin: 0 0.8rem 0 0;
          }

          span {
            font-size: 1.6rem;
          }

          :global(.rowItemBtn) {
            margin: 0 0 0 auto;
            min-width: fit-content;
          }
        }

        .settings {
          max-width: 24.7rem;
          margin: 2.2rem 0 0;
          padding: 2.2rem 0 0;
          border-top: 0.1rem solid var(--separator-line-color);

          button {
            color: var(--primary-color);
            font-size: 1.8rem;
            font-weight: 400;

            &:not(:last-child) {
              margin: 0 0 1rem;
            }
          }
        }

        .buttonsContainer {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
        }

        @media screen and (max-height: 640px) and (max-width: 1024px) {
          :global(.userCard) {
            padding-bottom: 5rem !important;
          }
        }

        @media screen and (max-height: 568px) and (max-width: 1024px) {
          :global(.userCard) {
            padding-bottom: 12rem !important;
          }
        }

        @media (max-width: 1024px) {
          .userCard {
            min-width: 100%;
            max-width: 100%;
            height: 100%;
            min-height: 70rem;
            overflow: scroll;
            padding-bottom: 3rem;

            .wrapper {
              padding: 5.5rem 1.25rem 0 1.45rem;
              box-shadow: none;
              min-height: 70rem;

              .backToAccount {
                margin: 1rem 0 0;
                color: #a5a8b9;
                line-height: 1.6rem;
                font-size: 1.2rem;
                font-weight: bold;
              }

              :global(.accountAvatar) {
                margin: 4.1rem auto 0;
              }

              .name {
                margin: 1.4rem 0 0;
                line-height: 2.7rem;
                font-size: 2rem;
              }

              .joinDate {
                margin: 0 0 2.6rem;
                line-height: 1.6rem;
                font-size: 1.2rem;
              }

              .rowItem span {
                font-size: 1.4rem;
              }

              .status-wrapper {
                margin: 0 0 2rem;
              }

              .confirmedRequisites {
                padding: 2rem 0 0;
              }

              .settings {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                max-width: 100%;
                padding: 1.65rem 0 0;

                button {
                  line-height: 1.9rem;
                  font-size: 1.4rem;
                }
              }

              .buttonsContainer {
                padding: 2.1rem 0;
                :global(button) {
                  width: calc(100% - 2.4rem);
                }
              }
            }
          }
        }
      `}</style>
    </div>
  )
}
